import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Search, MapPin, TrendingUp, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = "https://safesheatlas.com/api";

const RankingsPage = () => {
  const [rankings, setRankings] = useState([]);
  const [filteredRankings, setFilteredRankings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankings();
  }, []);

  useEffect(() => {
    filterAndSortRankings();
  }, [rankings, searchQuery, sortBy]);

  const fetchRankings = async () => {
    try {
      const response = await axios.get(`${API}/rankings/global`);
      setRankings(response.data);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortRankings = () => {
    let filtered = [...rankings];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.destination.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.destination.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    if (sortBy === 'score') {
      filtered.sort((a, b) => b.metrics.composite_wsi - a.metrics.composite_wsi);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.destination.city.localeCompare(b.destination.city));
    } else if (sortBy === 'trend') {
      filtered.sort((a, b) => b.metrics.trend_7day - a.metrics.trend_7day);
    }

    setFilteredRankings(filtered);
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-teal-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getRiskLevel = (score) => {
    if (score >= 80) return 'Low Risk';
    if (score >= 60) return 'Moderate';
    if (score >= 40) return 'Elevated';
    return 'High Risk';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="pt-24 pb-16" data-testid="rankings-page">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Global Safety Rankings</h1>
            <p className="text-base sm:text-lg text-gray-600">
              Compare women's safety across {rankings.length} top tourist destinations
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8" data-testid="filters-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search city or country..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-3"
                  data-testid="rankings-search-input"
                />
              </div>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="py-3" data-testid="sort-select">
                  <Filter className="w-5 h-5 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Highest Score</SelectItem>
                  <SelectItem value="name">City Name</SelectItem>
                  <SelectItem value="trend">Best Trend</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rankings Table */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4" data-testid="rankings-list">
              {filteredRankings.map((item, index) => (
                <Link
                  key={item.destination.id}
                  to={`/destination/${item.destination.id}`}
                  className="block hover-lift"
                  data-testid={`ranking-item-${index}`}
                >
                  <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center gap-6">
                      {/* Rank */}
                      <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-teal-400 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">#{index + 1}</span>
                      </div>

                      {/* Image */}
                      <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden hidden sm:block">
                        <img
                          src={item.destination.image_url}
                          alt={item.destination.city}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold mb-1">{item.destination.city}</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mb-2">
                          <MapPin className="w-4 h-4" />
                          {item.destination.country}
                        </p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                            {getRiskLevel(item.metrics.composite_wsi)}
                          </span>
                          {item.metrics.trend_7day !== 0 && (
                            <span
                              className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 ${
                                item.metrics.trend_7day > 0
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              <TrendingUp
                                className={`w-3 h-3 ${
                                  item.metrics.trend_7day < 0 ? 'rotate-180' : ''
                                }`}
                              />
                              {item.metrics.trend_7day > 0 ? '+' : ''}
                              {item.metrics.trend_7day?.toFixed(1) || '0.0'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Score */}
                      <div className="flex-shrink-0 text-center">
                        <div
                          className={`w-20 h-20 rounded-2xl ${getScoreClass(
                            item.metrics.composite_wsi
                          )} text-white flex flex-col items-center justify-center shadow-lg`}
                        >
                          <span className="text-2xl font-bold">
                            {item.metrics.composite_wsi?.toFixed(0) || '0'}
                          </span>
                          <span className="text-xs opacity-90">WSI</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}

              {filteredRankings.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-base">No destinations found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RankingsPage;